import { useEffect, useState, memo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  IconButton,
  Stack,
  Divider,
  Grid,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { useSearchICDs } from 'hooks/useLookups';
import { ICD } from 'types/types';
import { useFormContext, useWatch } from 'react-hook-form';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import AddIcon from 'icons/AddIcon';
import useStyles from './styles';
import EmptySearch from 'assets/emptySearch.png';

interface Props {
  name: 'icds' | 'diagnosisCodes';
  title: string;
}

function SelectICDs({ name, title }: Props) {
  const { setValue } = useFormContext();
  const selectedICDs: ICD[] = useWatch({ name });
  const styles = useStyles();
  const globalStyles = useGlobalStyles();

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [icds, setICDs] = useState<ICD[]>([]);
  const [apiSelected, setApiSelected] = useState<ICD[]>([]);

  const { refetch, isLoading } = useSearchICDs(keyword, {
    enabled: false,
    onSuccess: (data: ICD[]) => setICDs(data),
  });

  useEffect(() => {
    if (keyword.trim()) {
      refetch();
    } else {
      setICDs([]);
    }
  }, [keyword]);

  const openModal = () => {
    setOpen(true);
    setKeyword('');
    setICDs([]);
    setApiSelected(selectedICDs);
  };

  const toggleICD = (icd: ICD) => {
    setApiSelected(prev =>
      prev.some(i => i.id === icd.id)
        ? prev.filter(i => i.id !== icd.id)
        : [...prev, icd]
    );
  };

  const saveSelection = () => {
    setValue(name, apiSelected, { shouldDirty: true });
    setOpen(false);
  };

  useEffect(() => {
    if (!selectedICDs) return;
    setValue('icd_id', selectedICDs.map(icd => icd.id), { shouldDirty: true });
  }, [selectedICDs, setValue]);

  return (
    <>
      <Box className={globalStyles.sectionContainer}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className={globalStyles.sectionHeader}
        >
          <CustomText
            text="ICD-9/10"
            color={colors.primary}
            fontWeight={600}
          />

          <Box mr={2}>
            <IconButton
              onClick={openModal}
              className={globalStyles.sectionButton}
            >
              <AddIcon size={22} color={colors.primary} />
            </IconButton>
          </Box>
        </Stack>

        {selectedICDs.length > 0 && (
          <>
            <Divider />
            <Grid container spacing={1.5} sx={{ px: 2.5, my: 2 }}>
              {selectedICDs.map(icd => (
                <Grid size={{ xs: 12, md: 6 }} key={icd.id}>
                  <Box
                    key={icd.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={1}
                    borderRadius="10px"
                    border={`1px solid ${colors.itemBorder}`}
                  >
                    <Box p={0.5}>
                      <CustomText text={icd.code} color={colors.primary} />
                      <CustomText text={icd.value} color={colors.black} />
                    </Box>
                    <IconButton
                      color="error"
                      onClick={() =>
                        setValue(
                          name,
                          selectedICDs.filter(i => i.id !== icd.id),
                          { shouldDirty: true }
                        )
                      }
                    >
                      <RemoveCircleOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        className={globalStyles.modalContainer}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: '0px 12px 48px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              height: 600,
            },
          },
        }}
      >
        <DialogTitle className={globalStyles.modalTitle}>{title}</DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Stack spacing={2} sx={{ flex: 1, overflow: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Search"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />

            {icds.length == 0 && <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex={1}
              pt={3}
            >
              <img src={EmptySearch} alt="search icon" />
              <CustomText text={'No results to show'} fontSize={16} color={colors.gray} />
            </Box>}

            <List dense>
              {icds.map(icd => {
                const checked = apiSelected.some(i => i.id === icd.id);
                return (
                  <ListItem
                    key={icd.id}
                    onClick={() => toggleICD(icd)}
                    className={styles.resultItem}
                  >
                    <Checkbox checked={checked} />
                    <ListItemText
                      primary={icd.code}
                      secondary={icd.value}
                      sx={{ paddingLeft: '5px' }}
                    />
                  </ListItem>
                );
              })}
            </List>

            {apiSelected.length > 0 && (
              <>
                <Divider />
                <CustomText text="Selected ICDs" fontWeight={600} />
                <List dense>
                  {apiSelected.map(icd => (
                    <ListItem
                      key={icd.id}
                      className={styles.resultItem}
                      secondaryAction={
                        <IconButton
                          color="error"
                          onClick={() => toggleICD(icd)}
                          sx={{ padding: 0 }}
                        >
                          <RemoveCircleOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={icd.code}
                        secondary={icd.value}
                        sx={{ paddingLeft: '5px' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Stack>

          <CustomButton
            text='Save'
            onClick={saveSelection}
            fullWidth
          />

        </DialogContent>
      </Dialog>
    </>
  );
}

export default memo(SelectICDs);
